import { gql, useQuery } from '@apollo/client'
import ProdottoTemplate from 'components/templates/ProdottoTemplate/ProdottoTemplate'
import ProdottiListingTemplate from 'components/templates/ProdottiListingTemplate/ProdottiListingTemplate'
import { useEffect, useState } from 'react'
import { initializeApollo, queryPagination } from 'gql/client'
import {
  PRODOTTO,
  PREZZI_PRODOTTI,
  PRODOTTI_CORRELATI,
  CANTINA_BOX,
  CATEGORIA,
  ALLCATEGORIESPATHS,
  ALLPRODUCTSPATHS,
} from 'utils/queries'
import { LISTING } from 'utils/endpoint'
import { api } from 'utils'
import { getFiltriSlug, getCategoriaSlug } from 'utils/safe'
import { useRouter } from 'next/router'
import { getIconByChiave } from 'utils/icon'
import useUtente from 'hooks/useUtente'
import { updateGTMDataLayer } from 'utils/gtm'
import { NextWrapper } from 'data-fetching-wrapper'
import { REDIRECTS } from 'utils/endpoint'

const ProdottoDetail = ({ data, page }) => {
  const [correlati, setCorrelati] = useState([])
  const [cantina, setCantina] = useState()
  const { utente, loading: loadingUtente } = useUtente()
  const router = useRouter()

  const prodotto = data

  const { data: dataPrezzo, loading: loadingPrezzo } = useQuery(
    gql`
      ${PREZZI_PRODOTTI}
    `,
    {
      variables: { idProdotti: [prodotto.pk] },
    }
  )

  const [prodottoPrezzo, setProdottoPrezzo] = useState(prodotto)

  useEffect(() => {
    if (!loadingPrezzo && dataPrezzo?.prezziProdotti?.length) {
      setProdottoPrezzo({
        ...prodotto,
        ...dataPrezzo.prezziProdotti[0],
      })
    }
  }, [loadingPrezzo])

  const { data: dataCorrelati, loading: loadingCorrelati } = useQuery(
    gql`
      ${PRODOTTI_CORRELATI.replace('__slug__', prodotto.slug)},
    `,
    { fetchPolicy: 'cache-first' }
  )

  useEffect(() => {
    if (!loadingCorrelati && dataCorrelati)
      setCorrelati(dataCorrelati.prodotto?.prodotto?.correlati)
  }, [loadingCorrelati])

  if (prodotto.cantina) {
    const { data: dataCantina, loading: loadingCantina } = useQuery(
      gql`
        ${CANTINA_BOX.replace('__slug__', prodotto.cantina.slug)},
      `,
      { fetchPolicy: 'cache-first' }
    )

    useEffect(() => {
      if (!loadingCantina && dataCantina) setCantina(dataCantina.cantina)
    }, [loadingCantina])
  }

  useEffect(() => {
    if (!loadingUtente) {
      updateGTMDataLayer({ pagina: page }, utente, router, data)
    }
  }, [loadingUtente])

  return (
    <ProdottoTemplate
      prodotto={prodottoPrezzo}
      prodottiCorrelati={correlati}
      cantina={cantina}
      page={page}
    />
  )
}

const CategoriaDetail = ({ data, page, listing }) => {
  const getSottoCategorie = (categoria) => {
    return categoria?.sottoCategorie.map((o) => ({
      ...o,
      icon: getIconByChiave(o.chiave),
      active: o.slug === categoria.slug,
    }))
  }

  const categoria = data
  const sottocategorie = getSottoCategorie(categoria)
  const cantina = categoria?.cantina

  return (
    <ProdottiListingTemplate
      sottocategorie={sottocategorie}
      categoria={categoria}
      pagina={page}
      cantina={cantina}
      listing={listing}
      //loading={loading}
    />
  )
}

function Detail({ isSchedaProdotto, data, pagina, listing }) {
  if (isSchedaProdotto) {
    return <ProdottoDetail data={data} page={pagina} key={data.pk} />
  } else {
    return <CategoriaDetail data={data} page={pagina} listing={listing} />
  }
}

export const getStaticPaths = NextWrapper.getStaticPaths(async ({ locales }) => {
  let paths = []
  const maxPathsNumber = 10
  for (const locale of locales) {
    let categoriesNumberAdded = 0
    let productsNumberAdded = 0
    const client = initializeApollo(null, null, null, locale)
    let categories = await queryPagination(client, 'categorie', ALLCATEGORIESPATHS)
    let products = await queryPagination(client, 'prodotti', ALLPRODUCTSPATHS)
    categories.forEach((item) => {
      if (categoriesNumberAdded < maxPathsNumber) {
        paths.push({ params: { slug: [item.slugTree] }, locale: locale })
        categoriesNumberAdded += 1
      }
    })
    products.forEach((item) => {
      if (!['301', '302'].includes(item.stato.azione)) {
        if (productsNumberAdded < maxPathsNumber) {
          paths.push({ params: { slug: [item.slug + '.html'] }, locale: locale })
          productsNumberAdded += 1
        }
      }
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}, '/i-vini/[slug]')

export const getStaticProps = NextWrapper.getStaticProps(async ({ params: { slug }, locale }) => {
  const client = initializeApollo(null, null, null, locale)

  let isSchedaProdotto = false
  let data = null
  let pagina = null
  let listing = null

  if (slug.length === 1 && slug[0].includes('.html')) {
    // SCHEDA-PRODOTTO
    isSchedaProdotto = true
    const prodottoSlug = slug[0].slice(0, -5)

    const prodottoData = await client.query({
      query: gql`
        ${PRODOTTO.replace('__slug__', prodottoSlug)}
      `,
    })

    const redirect = prodottoData.data?.prodotto?.redirect
    const prodotto = prodottoData.data.prodotto?.prodotto

    if (redirect) {
      let code_redirect = 302
      if (prodottoData.data?.prodotto?.statusCodeRedirect == '301') {
        code_redirect = 301
      }

      return {
        redirect: {
          destination: redirect.url,
          statusCode: code_redirect,
        },
      }
    }

    if (!prodotto) {
      return {
        notFound: true,
      }
    }
    data = prodotto
    pagina = prodotto.pagina
  } else {
    // CATEGORIA
    isSchedaProdotto = false
    const categoriaData = await client.query({
      query: gql`
        ${CATEGORIA.replace('__slugTree__', getCategoriaSlug(slug).join('/'))}
      `,
    })

    const categoria = categoriaData.data.categoria
    if (!categoria) {
      return {
        notFound: true,
      }
    }
    data = categoria
    pagina = categoria.pagina

    const params = {
      id_categoria: categoria.pk,
      lang: locale,
    }

    const filtri_slug = getFiltriSlug(slug) // es. ['basilicata', 'bolla']
    if (filtri_slug && filtri_slug.length) {
      params['filtri_slug'] = filtri_slug.join('/') // es. 'basilicata/bolla'
    }

    const response = await api.get(LISTING, { params })
    listing = response.data?.listing

    if (!listing) {
      return {
        notFound: true,
      }
    }
  }
  return {
    props: { isSchedaProdotto, data, pagina, listing, key: data.pk },
    revalidate: 60 * 10,
  }
}, '/i-vini/[slug]')

export default Detail
